import React, {useState, useEffect, useRef} from 'react';
import {API_ENDPOINTS} from "../../../apiConfig";
import API from "../../../utils/API";
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Forms from "../../../components/Forms";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import EditIconButton from "../../../components/Forms/EditIconButton";
import PartModal from "../../../components/PartModal";
import {useModal} from "../../../contexts/ModalContext";
import AdminDataView from "../../../components/AdminDataView";

const Parts = () => {
    const [partList, setPartList] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [filterText, setFilterText] = useState(''); // Stav pro uložení filtračního textu
    const [selectedCategories, setSelectedCategories] = useState([]); // Stav pro zaškrtnuté kategorie
    const [availableCategories, setAvailableCategories] = useState([]); // K dispozici kategorie pro filtr
    const {t} = useTranslation();
    const [error, setError] = useState();
    const {openModal, confirm, stack} = useModal();

    const filterInputRef = useRef(null); // Ref pro textové pole filtru

    const fetchParts = async () => {
        const response = await API.get(API_ENDPOINTS.partSku.list);
        setPartList(response.data);
        setFilteredCategories(response.data);
        const uniqueCategories = [
            ...new Set(response.data.flatMap((partSku) => (partSku?.part?.categoryList || []).map((cat) => cat.name)))
        ];
        setAvailableCategories(uniqueCategories);
    };

    useEffect(() => {
        fetchParts();
    }, []);

    useEffect(() => {
        const filtered = partList.filter((part) => {
            const filterLowerCase = filterText.toLowerCase();
            const master = part.part || {};

            const matchesName = (part.name || master.name || '').toLowerCase().includes(filterLowerCase);

            const matchesCategory = (master.categoryList || []).some(cat =>
                cat.name.toLowerCase().includes(filterLowerCase) ||
                cat.code?.toLowerCase().includes(filterLowerCase)
            );

            const matchesCategorySelection = selectedCategories.length === 0 ||
                (master.categoryList || []).some(cat => selectedCategories.includes(cat.name));

            return (matchesName || matchesCategory) && matchesCategorySelection;
        });
        setFilteredCategories(filtered);
    }, [filterText, selectedCategories, partList]);

    useEffect(() => {
        if (stack.length > 0) return;
        const handleKeyDown = (e) => {
            if (!e.shiftKey && filterInputRef.current && document.activeElement !== filterInputRef.current) {
                filterInputRef.current.focus();
            }

            if (e.shiftKey && e.key === 'N') {
                e.preventDefault();
                openPartModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [stack.length]);

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Escape') {
            setFilterText('');
        }
    };

    const handleCategoryChange = (category) => {
        if (selectedCategories.includes(category)) {
            setSelectedCategories(selectedCategories.filter((cat) => cat !== category));
        } else {
            setSelectedCategories([...selectedCategories, category]);
        }
    };

    const renderPurchasePrice = (part) => {
        const price = part.averagePurchasePricePerUnit ?? part.expectedPurchasePricePerUnit;
        return price ? `${price.gross} ${price.currency}` : '-';
    }

    const columns = [
        {
            key: 'name',
            name: t('name'),
            renderCell: ({row}) => row.name || row?.part?.name || '-',
            minWidth: 300
        },
        {
            key: 'code',
            name: t('deviceCode'),
            renderCell: ({row}) => (row?.part?.compatibleWith || []).map(dev => dev.code).join(', ')
        },
        {
            key: 'compatibleWith',
            name: t('compatibleWith'),
            renderCell: ({row}) => (row?.part?.compatibleWith || []).map(dev => dev.name).join(', ')
        },
        {key: 'storagePlace', name: t('storagePlace')},
        {key: 'purchasePrice', name: t('purchasePrice'), renderCell: ({row}) => renderPurchasePrice(row)},
        {key: 'stockQuantity', name: t('stockQuantity'), minWidth: 80},
        {key: 'reorderThreshold', name: t('limit'), minWidth: 80},
        {key: 'description', name: t('description')},
        {
            key: 'categoryList',
            name: t('categoryList'),
            renderCell: ({row}) => (row?.part?.categoryList || []).map(cat => cat.name).join(', ')
        },
        {
            key: 'createdBy',
            name: t('createdBy'),
            renderCell: ({row}) => `${row.createdBy.name} ${row.createdBy.surname}`,
            minWidth: 130
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            renderCell: ({row}) => moment(row.createdAt).format('DD.MM.YYYY HH:mm'),
            minWidth: 130
        },
        {
            key: 'actions',
            name: 'Akce',
            width: 110,
            renderCell: ({row}) => (
                <div className="flex items-center justify-end h-full w-full">
                    <EditIconButton title="Upravit díl" onClick={() => openPartModal(row)}/>
                    <DeleteIconButton title="Smazat díl" onClick={() => confirmDeletePart(row)}/>
                </div>
            )
        }
    ].map(item => {
        item.resizable = true;
        return item;
    });

    const confirmDeletePart = async (part) => {
        const result = await confirm(`Opravdu chcete zrušit opravu ${part._id}?`);
        if (result === true) {
            try {
                await API.delete(API_ENDPOINTS.partSku.delete(part._id));
                setPartList(partList.filter(category => category._id !== part._id));
            } catch (error) {
                setError(error);
                console.error("Failed to delete part:", error);
            }
        }
    };

    const openPartModal = (part) => {
        openModal(PartModal, {part, onPartUpdate: fetchParts}, {title: 'test'});
    };

    return (
        <div>
            <Forms.Button label={t('addPart')} onClick={() => openPartModal()} className="mb-8"/>

            <div className="grid grid-cols-4 items-end gap-4 mb-4">
                <Forms.Text
                    ref={filterInputRef}
                    value={filterText}
                    onChange={val => setFilterText(val)}
                    onKeyDown={handleFilterKeyDown}
                    label={t('name')}
                    placeholder={t('searchByName')}
                />
                <div className="self-end col-span-2">
                    <label className="mb-2 block">{t('filterByCategory')}</label>
                    <div className="category-filters grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                        {React.Children.toArray(
                            availableCategories.map((category) => (
                                <Forms.Checkbox
                                    key={category}
                                    label={category}
                                    checked={selectedCategories.includes(category)}
                                    onChange={() => handleCategoryChange(category)}
                                />
                            ))
                        )}
                    </div>
                </div>

            </div>
            <AdminDataView section="parts" rows={filteredCategories} columns={columns}/>
        </div>
    );
};

export default Parts;
